import React, { useState, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { Download, FileText, RotateCcw, Copy, ChevronDown, ChevronUp, Printer, AlertTriangle, Lock, Crown } from 'lucide-react';
import ResearchDesignDiagram from './ResearchDesignDiagram';
import ResearchDecisionFlow from './ResearchDecisionFlow';

const useEnsureWizardComplete = () => {
  const { currentWizard, completeWizard, wizardAComplete, wizardBComplete, wizardCComplete } = useWizard();
  
  useEffect(() => {
    if (currentWizard === 'A' && !wizardAComplete) {
      completeWizard('A');
    } else if (currentWizard === 'B' && !wizardBComplete) {
      completeWizard('B');
    } else if (currentWizard === 'C' && !wizardCComplete) {
      completeWizard('C');
    }
  }, [currentWizard, completeWizard, wizardAComplete, wizardBComplete, wizardCComplete]);
};

const renderReportAsHTML = (text) => {
  if (!text) return null;
  
  const lines = text.split('\n');
  const elements = [];
  let currentParagraph = [];
  let listItems = [];
  let inWarningBox = false;
  let warningContent = [];
  let keyIndex = 0;
  
  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const content = currentParagraph.join('\n').trim();
      if (content) {
        elements.push(<p key={keyIndex++} className="report-paragraph">{content}</p>);
      }
      currentParagraph = [];
    }
  };
  
  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={keyIndex++} className="report-list">
          {listItems.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      );
      listItems = [];
    }
  };
  
  const flushWarning = () => {
    if (warningContent.length > 0) {
      elements.push(
        <div key={keyIndex++} className="report-warning-box">
          <AlertTriangle size={20} />
          <div>{warningContent.map((line, i) => <p key={i}>{line}</p>)}</div>
        </div>
      );
      warningContent = [];
      inWarningBox = false;
    }
  };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    
    if (trimmedLine.startsWith('===') && trimmedLine.endsWith('===') && trimmedLine.length > 10) {
      continue;
    }
    
    if (trimmedLine.startsWith('***') && trimmedLine.includes('WARNING') && trimmedLine.endsWith('***')) {
      flushParagraph();
      flushList();
      inWarningBox = true;
      continue;
    }
    
    if (inWarningBox && trimmedLine.startsWith('***') && trimmedLine.endsWith('***')) {
      flushWarning();
      continue;
    }
    
    if (inWarningBox) {
      if (trimmedLine && !trimmedLine.startsWith('*')) {
        warningContent.push(trimmedLine);
      }
      continue;
    }
    
    if (trimmedLine.match(/^SECTION \d+:/)) {
      flushParagraph();
      flushList();
      const sectionTitle = trimmedLine.replace(/^SECTION \d+:\s*/, '');
      elements.push(<h3 key={keyIndex++} className="report-section-title">{sectionTitle}</h3>);
      continue;
    }
    
    if (trimmedLine.match(/^---.*---$/)) {
      flushParagraph();
      flushList();
      const subTitle = trimmedLine.replace(/^---\s*/, '').replace(/\s*---$/, '');
      elements.push(<h4 key={keyIndex++} className="report-subsection-title">{subTitle}</h4>);
      continue;
    }
    
    if (trimmedLine.match(/^[A-Z][A-Z\s]+:$/) && trimmedLine.length < 60) {
      flushParagraph();
      flushList();
      elements.push(<h4 key={keyIndex++} className="report-heading">{trimmedLine.replace(/:$/, '')}</h4>);
      continue;
    }
    
    if (trimmedLine.startsWith('- ')) {
      flushParagraph();
      listItems.push(trimmedLine.substring(2));
      continue;
    }
    
    if (trimmedLine.match(/^\d+\.\s+[A-Z]/) && trimmedLine.length < 80) {
      flushParagraph();
      flushList();
      elements.push(<p key={keyIndex++} className="report-numbered-item"><strong>{trimmedLine}</strong></p>);
      continue;
    }
    
    if (listItems.length > 0 && !trimmedLine.startsWith('- ')) {
      flushList();
    }
    
    if (trimmedLine.includes(': ') && trimmedLine.split(': ')[0].length < 30 && !trimmedLine.startsWith('-')) {
      const colonIndex = trimmedLine.indexOf(': ');
      const label = trimmedLine.substring(0, colonIndex);
      const value = trimmedLine.substring(colonIndex + 2);
      if (label.match(/^[A-Za-z\s()]+$/) && label.length < 25) {
        flushParagraph();
        elements.push(
          <div key={keyIndex++} className="report-field">
            <span className="report-field-label">{label}:</span>
            <span className="report-field-value">{value}</span>
          </div>
        );
        continue;
      }
    }
    
    if (trimmedLine === '' || trimmedLine.match(/^[=\-*]+$/)) {
      flushParagraph();
      continue;
    }
    
    currentParagraph.push(trimmedLine);
  }
  
  flushParagraph();
  flushList();
  flushWarning();
  
  return <div className="report-html-content">{elements}</div>;
};

const FinalReport = () => {
  useEnsureWizardComplete();
  const { dataA, dataB, decisions, wizardAComplete, wizardBComplete, resetWizard, switchWizard, currentWizard } = useWizard();
  const { canGenerateReport, triggerUpgradeModal } = useSubscription();
  const [partAReport, setPartAReport] = useState('');
  const [copySuccessA, setCopySuccessA] = useState(false);
  const [copySuccessB, setCopySuccessB] = useState(false);
  const [showPartAPreview, setShowPartAPreview] = useState(true);
  const [showPartBPreview, setShowPartBPreview] = useState(true);
  const [diagramView, setDiagramView] = useState('simple');

  const reportKey = currentWizard === 'A' ? 'A' : currentWizard === 'B' ? 'B' : currentWizard === 'C' ? 'C' : 'A';
  const canAccessReport = canGenerateReport(reportKey);

  const getDimensionLabel = (dimension, value) => {
    const labels = {
      ontology: {
        realist: 'Realism',
        relativist: 'Relativism',
        pragmatic: 'Pragmatic'
      },
      epistemology: {
        objectivist: 'Objectivism',
        subjectivist: 'Subjectivism',
        critical: 'Critical / Stratified',
        practical: 'Practical Knowledge'
      },
      axiology: {
        value_free: 'Value-Free',
        value_laden: 'Value-Laden',
        value_driven: 'Value-Driven',
        pragmatic_values: 'Pragmatic'
      }
    };
    return labels[dimension]?.[value] || value || 'Not specified';
  };

  const getParadigmLabel = (value) => {
    const labels = {
      positivism: 'Positivism',
      interpretivism: 'Interpretivism',
      pragmatism: 'Pragmatism',
      critical_realism: 'Critical Realism',
      transformative: 'Transformative'
    };
    return labels[value] || value;
  };

  const getDataCollectionLabel = (value) => {
    const labels = {
      structuredSurvey: 'Structured Surveys/Questionnaires',
      experiments: 'Experimental Procedures',
      structuredObs: 'Structured Observation',
      secondaryData: 'Secondary/Administrative Data',
      tests: 'Standardized Tests/Scales',
      semiStructuredInt: 'Semi-Structured Interviews',
      structuredInt: 'Structured Interviews',
      focusGroups: 'Focus Groups',
      participantObs: 'Participant Observation',
      documents: 'Document Analysis',
      fieldNotes: 'Field Notes/Journals',
      visualMethods: 'Visual Methods',
      interviews: 'Interviews',
      surveys: 'Surveys/Questionnaires',
      observation: 'Observation'
    };
    return labels[value] || value;
  };

  const getSamplingLabel = (value) => {
    const labels = {
      simpleRandom: 'Simple Random Sampling',
      systematic: 'Systematic Sampling',
      stratified: 'Stratified Sampling',
      cluster: 'Cluster Sampling',
      multistage: 'Multistage Sampling',
      pps: 'Probability Proportional to Size (PPS)',
      convenience: 'Convenience Sampling',
      purposive: 'Purposive (Judgmental) Sampling',
      quota: 'Quota Sampling',
      snowball: 'Snowball Sampling',
      volunteer: 'Volunteer/Self-Selection Sampling',
      expert: 'Expert Sampling',
      theoretical: 'Theoretical Sampling',
      homogeneous: 'Homogeneous Sampling',
      maxVariation: 'Maximum Variation Sampling',
      criticalCase: 'Critical Case Sampling',
      sequential: 'Sequential Sampling',
      adaptive: 'Adaptive Sampling',
      rds: 'Respondent-Driven Sampling (RDS)',
      panel: 'Panel Sampling'
    };
    return labels[value] || value;
  };

  const getAnalysisLabel = (value) => {
    const labels = {
      descriptiveStats: 'Descriptive Statistics',
      inferentialStats: 'Inferential Statistics',
      regression: 'Regression Analysis',
      anova: 'ANOVA/ANCOVA',
      factorAnalysis: 'Factor Analysis',
      sem: 'Structural Equation Modeling',
      timeSeries: 'Time Series Analysis',
      thematicAnalysis: 'Thematic Analysis',
      contentAnalysis: 'Content Analysis',
      groundedTheoryCoding: 'Grounded Theory Coding',
      narrativeAnalysis: 'Narrative Analysis',
      discourseAnalysis: 'Discourse Analysis',
      phenomenologicalAnalysis: 'Phenomenological Analysis',
      frameworkAnalysis: 'Framework Analysis',
      ipa: 'Interpretative Phenomenological Analysis (IPA)'
    };
    return labels[value] || value;
  };

  const getQualityCriteriaLabel = (value) => {
    const labels = {
      internalValidity: 'Internal Validity',
      externalValidity: 'External Validity',
      reliability: 'Reliability',
      constructValidity: 'Construct Validity',
      statConclusion: 'Statistical Conclusion Validity',
      credibility: 'Credibility',
      transferability: 'Transferability',
      dependability: 'Dependability',
      confirmability: 'Confirmability',
      authenticity: 'Authenticity',
      resonance: 'Resonance',
      triangulation: 'Triangulation',
      validity: 'Validity',
      memberChecking: 'Member Checking',
      reflexivity: 'Reflexivity'
    };
    return labels[value] || value;
  };

  const getOntologyDescription = (value) => {
    const descriptions = {
      realist: `You believe that reality exists independently of human perception. There is one true reality that can be discovered through systematic observation.`,
      relativist: `You believe that multiple realities exist, constructed through social interaction. What is "real" depends on perspective and context.`,
      pragmatic: `You believe that reality is what helps solve problems. Both objective and subjective perspectives have value depending on the research question.`
    };
    return descriptions[value] || '';
  };

  const getEpistemologyDescription = (value) => {
    const descriptions = {
      objectivist: `You believe knowledge comes from direct observation and measurement. Researchers should maintain a detached, neutral stance.`,
      subjectivist: `You believe knowledge is co-constructed between researcher and participants. Understanding comes from interpreting meanings.`,
      critical: `You believe knowledge involves uncovering hidden mechanisms beneath observable reality. Both empirical and interpretive approaches are needed.`,
      practical: `You believe knowledge is what helps us act effectively. Multiple ways of knowing are valid if they solve problems.`
    };
    return descriptions[value] || '';
  };

  const getAxiologyDescription = (value) => {
    const descriptions = {
      value_free: `You believe research should be objective and free from researcher values. Facts must be separated from values.`,
      value_laden: `You believe research is inherently influenced by values. Researchers should acknowledge and reflect on their biases.`,
      value_driven: `You believe research should explicitly pursue values like social justice and empowerment of marginalized groups.`,
      pragmatic_values: `You believe values guide research problem selection but should not bias interpretation. Focus on practical outcomes.`
    };
    return descriptions[value] || '';
  };

  const getSampleSizeMethodCitation = () => {
    const method = dataB?.sampleSizeCalculator?.method;
    const citations = {
      cochran: 'Cochran (1977)',
      krejcie: 'Krejcie & Morgan (1970)',
      slovin: 'Slovin (1960)'
    };
    return citations[method] || null;
  };

  const getSampleSizeDisplay = (includeCalculatorInfo = false) => {
    const sampleSizes = dataA.techniques?.sampleSizesPerMethod;
    const calculatorSize = dataB?.sampleSizeCalculator?.calculatedSize;
    const calculatorMethod = dataB?.sampleSizeCalculator?.method;
    
    let sizeDisplay = '';
    
    if (sampleSizes && Object.keys(sampleSizes).length > 0) {
      const sizes = Object.values(sampleSizes).filter(s => s && parseInt(s) > 0);
      if (sizes.length === 1) {
        sizeDisplay = `N = ${sizes[0]}`;
      } else if (sizes.length > 1) {
        const total = sizes.reduce((sum, s) => sum + parseInt(s), 0);
        sizeDisplay = `N = ${total} (combined from all data sources)`;
      }
    } else if (dataA.techniques?.samplingDetails?.targetSampleSize) {
      sizeDisplay = `N = ${dataA.techniques.samplingDetails.targetSampleSize}`;
    } else if (calculatorSize) {
      sizeDisplay = `N = ${calculatorSize}`;
    } else {
      return 'Not specified';
    }
    
    if (includeCalculatorInfo && calculatorMethod) {
      const citation = getSampleSizeMethodCitation();
      if (citation) {
        sizeDisplay += ` (calculated using ${citation})`;
      }
    }
    
    return sizeDisplay;
  };

  const getSampleSizeJustification = () => {
    const calculator = dataB?.sampleSizeCalculator;
    if (!calculator?.method || !calculator?.calculatedSize) {
      return null;
    }
    
    const methodDescriptions = {
      cochran: `The sample size of N = ${calculator.calculatedSize} was determined using Cochran's formula (Cochran, 1977), which accounts for population size, confidence level, margin of error, and population proportion. This method is widely recommended for survey research and provides a statistically rigorous basis for sample size determination.`,
      krejcie: `The sample size of N = ${calculator.calculatedSize} was determined using the Krejcie and Morgan (1970) table, which provides pre-calculated sample sizes based on a 95% confidence level and 5% margin of error. This method is frequently cited in educational and social science research.`,
      slovin: `The sample size of N = ${calculator.calculatedSize} was determined using Slovin's formula (Slovin, 1960), which calculates the minimum sample size based on population size and acceptable margin of error. This straightforward approach is commonly used when population parameters are unknown.`
    };
    
    return methodDescriptions[calculator.method] || null;
  };

  useEffect(() => {
    if (wizardAComplete) {
      const report = generatePartAReport();
      setPartAReport(report);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardAComplete, dataA]);

  const handleCopyPartA = () => {
    navigator.clipboard.writeText(partAReport);
    setCopySuccessA(true);
    setTimeout(() => setCopySuccessA(false), 2000);
  };

  const handleCopyPartB = () => {
    navigator.clipboard.writeText(dataB.analysisPlan || '');
    setCopySuccessB(true);
    setTimeout(() => setCopySuccessB(false), 2000);
  };

  const handleDownloadPartA = () => {
    const element = document.createElement("a");
    const file = new Blob([partAReport], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = "research_methodology_report.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleDownloadPartB = () => {
    const element = document.createElement("a");
    const file = new Blob([dataB.analysisPlan || ''], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = "statistical_analysis_plan.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleDownloadFull = () => {
    const fullReport = generateFullReport();
    const element = document.createElement("a");
    const file = new Blob([fullReport], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = "complete_research_design_report.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const generatePartAReport = () => {
    let report = '';

    report += `================================================================================
                    RESEARCH METHODOLOGY REPORT
                Based on Saunders' Research Onion Framework
                    & Creswell's Research Design
================================================================================

`;

    report += `********************************************************************************
                              ⚠️ IMPORTANT WARNING ⚠️
********************************************************************************

This report has been auto-generated based on your selections in the Research 
Methodology Wizard. The content below uses standardized academic language and 
definitions that are commonly used in research methodology literature.

BEFORE USING THIS CONTENT IN YOUR WORK:

1. PARAPHRASE: Rewrite the explanations in your own words. Other researchers 
   using similar methodological approaches may generate identical or very 
   similar content from this tool.

2. CITE PRIMARY SOURCES: Reference the original methodologists (Saunders, 
   Creswell, Bryman, etc.) directly rather than relying solely on this summary.

3. CUSTOMIZE: Adapt the generic explanations to your specific research context, 
   discipline, and research questions.

4. VERIFY: Cross-check all methodological claims against current academic 
   sources in your field.

Failure to paraphrase may result in similarity with other users' submissions.

********************************************************************************

Generated: ${new Date().toLocaleString()}

`;

    report += `================================================================================
EXECUTIVE SUMMARY
================================================================================

This research methodology report documents the philosophical foundations, 
theoretical approach, methodological choices, research strategy, time horizon, 
and data collection techniques selected for this study. Each decision is 
grounded in established research methodology frameworks, primarily Saunders 
et al.'s Research Onion, Creswell's Research Design typology, Sample Size 
Calculation Methods (Cochran, Krejcie, or Slovin) and best practices in Research Ethics.

Research Design at a Glance:
----------------------------
${dataA.philosophicalDimensions?.ontology ? `Ontology:            ${getDimensionLabel('ontology', dataA.philosophicalDimensions.ontology)}` : ''}
${dataA.philosophicalDimensions?.epistemology ? `Epistemology:        ${getDimensionLabel('epistemology', dataA.philosophicalDimensions.epistemology)}` : ''}
${dataA.philosophicalDimensions?.axiology ? `Axiology:            ${getDimensionLabel('axiology', dataA.philosophicalDimensions.axiology)}` : ''}
Paradigm:            ${getParadigmLabel(dataA.philosophy) || 'Not specified'}
Theory Approach:     ${dataA.theoryApproach || 'Not specified'}
Methodology:         ${dataA.methodologicalChoice || 'Not specified'}
Strategy:            ${dataA.strategy || 'Not specified'}
Time Horizon:        ${dataA.timeHorizon || 'Not specified'}
Sample Size Target:  ${getSampleSizeDisplay(true)}

`;

    report += generatePhilosophySection();
    report += generateTheoryApproachSection();
    report += generateMethodologicalChoiceSection();
    report += generateStrategySection();
    report += generateTimeHorizonSection();
    report += generateTechniquesSection();
    report += generateCoherenceSection();
    report += generateLimitationsSection();

    report += `
================================================================================
                            END OF PART A REPORT
================================================================================

This methodology report provides a comprehensive framework for your research 
design. Use this document as a foundation for writing your methodology chapter, 
ensuring you paraphrase content and add discipline-specific details.

For data analysis planning, complete Wizard B to generate a complementary 
Data Analysis Plan.

`;

    return report;
  };

  const generatePhilosophySection = () => {
    const dims = dataA.philosophicalDimensions || {};
    
    let section = `
================================================================================
SECTION 1: PHILOSOPHICAL FOUNDATIONS
================================================================================

The philosophical stance underpinning a research study shapes every subsequent 
methodological decision. Philosophy determines what counts as valid knowledge 
(epistemology), what exists and can be studied (ontology), and how values 
influence research (axiology). Selecting an appropriate philosophy ensures 
internal consistency throughout the research design.

--- YOUR PHILOSOPHICAL DIMENSIONS ---

`;

    if (dims.ontology || dims.epistemology || dims.axiology) {
      section += `Before selecting a research paradigm, you established your position on 
three fundamental philosophical dimensions:

`;
      if (dims.ontology) {
        section += `ONTOLOGY (Nature of Reality): ${getDimensionLabel('ontology', dims.ontology)}
${getOntologyDescription(dims.ontology)}

`;
      }
      if (dims.epistemology) {
        section += `EPISTEMOLOGY (Nature of Knowledge): ${getDimensionLabel('epistemology', dims.epistemology)}
${getEpistemologyDescription(dims.epistemology)}

`;
      }
      if (dims.axiology) {
        section += `AXIOLOGY (Role of Values): ${getDimensionLabel('axiology', dims.axiology)}
${getAxiologyDescription(dims.axiology)}

`;
      }
    }

    section += `--- YOUR SELECTED PARADIGM ---

`;

    const philosophyDetails = {
      positivism: {
        title: 'POSITIVISM',
        description: `Positivism is a philosophical stance that emphasizes the importance of 
observable, measurable phenomena and the application of natural science methods 
to social research. Rooted in the work of Auguste Comte and later refined by 
logical positivists, this paradigm assumes that reality is objective, external 
to the researcher, and can be discovered through systematic empirical inquiry.

Ontological Position (Nature of Reality):
Positivists adopt a realist ontology, asserting that a single, objective reality 
exists independently of human perception. This reality operates according to 
universal laws that can be discovered through careful observation and measurement. 
Social phenomena, like natural phenomena, are seen as having underlying regularities 
that researchers can identify and predict.

Epistemological Position (What Counts as Knowledge):
Knowledge is derived from direct observation and measurement. Only phenomena that 
can be empirically verified through the senses (or instruments extending the senses) 
constitute legitimate scientific knowledge. The researcher maintains a detached, 
objective stance, minimizing personal bias and values in the research process.

Axiological Position (Role of Values):
Positivism strives for value-free research. The researcher attempts to remain 
neutral and objective, separating facts from values. Research findings should be 
replicable regardless of who conducts the study.

Implications for Your Research:
- You will likely use quantitative methods with statistical analysis
- Your research will seek to test hypotheses derived from existing theory
- You will aim for large, representative samples to enable generalization
- Validity and reliability will be assessed through statistical measures
- Results will be presented as objective facts, minimizing researcher interpretation`,

        alignment: `This philosophy aligns well with:
- Experimental and quasi-experimental designs
- Survey research with closed-ended questions
- Deductive reasoning (theory → hypotheses → testing)
- Statistical analysis and hypothesis testing
- Research seeking causal explanations or predictions`,

        citations: `Key Authors to Cite:
- Comte, A. (1853) - The Positive Philosophy
- Popper, K. (1959) - The Logic of Scientific Discovery
- Bryman, A. (2016) - Social Research Methods, Chapter 1
- Saunders, M., Lewis, P., & Thornhill, A. (2019) - Research Methods for 
  Business Students, Chapter 4`
      },

      interpretivism: {
        title: 'INTERPRETIVISM',
        description: `Interpretivism (also called constructivism or social constructionism) is 
a philosophical paradigm that emphasizes the subjective nature of social reality 
and the importance of understanding human experience from the perspective of 
those being studied. This approach emerged as a critique of positivism's 
application of natural science methods to social phenomena.

Ontological Position (Nature of Reality):
Interpretivists adopt a relativist ontology, asserting that multiple realities 
exist, constructed through social interaction and individual interpretation. 
Reality is not "out there" waiting to be discovered but is actively created 
through human meaning-making processes. What is considered "real" varies across 
cultures, contexts, and individuals.

Epistemological Position (What Counts as Knowledge):
Knowledge is subjective and emerges from the researcher's interaction with 
participants. Understanding social phenomena requires empathic interpretation 
of the meanings, beliefs, and intentions of social actors. The researcher cannot 
be fully objective; their interpretations are shaped by their own experiences 
and perspectives.

Axiological Position (Role of Values):
Research is inherently value-laden. The researcher's values, background, and 
theoretical lens influence what questions are asked, how data is interpreted, 
and what conclusions are drawn. Rather than hiding these influences, interpretivist 
researchers often practice reflexivity—explicitly acknowledging their positionality.

Implications for Your Research:
- You will likely use qualitative methods (interviews, observations, documents)
- Your research will seek to understand meanings, experiences, and perspectives
- Sample sizes will be smaller but studied in greater depth
- Trustworthiness is assessed through credibility, transferability, and dependability
- Findings are presented as interpretations, acknowledging alternative readings`,

        alignment: `This philosophy aligns well with:
- Phenomenological studies exploring lived experience
- Ethnographic research immersed in cultural contexts
- Grounded theory building theory from participant perspectives
- Case studies examining phenomena in context
- Narrative research analyzing stories and meaning-making`,

        citations: `Key Authors to Cite:
- Weber, M. (1947) - Theory of Social and Economic Organization (Verstehen)
- Berger, P., & Luckmann, T. (1966) - The Social Construction of Reality
- Geertz, C. (1973) - The Interpretation of Cultures (thick description)
- Creswell, J. W. (2013) - Qualitative Inquiry and Research Design`
      },

      pragmatism: {
        title: 'PRAGMATISM',
        description: `Pragmatism is a philosophical tradition that evaluates ideas and theories 
based on their practical consequences and usefulness in solving problems. Rather 
than engaging in abstract debates about the nature of reality, pragmatists focus 
on "what works" to address the research question at hand. This philosophy emerged 
from the work of American philosophers Charles Sanders Peirce, William James, 
and John Dewey.

Ontological Position (Nature of Reality):
Pragmatists adopt a pluralist stance, accepting that both objective and subjective 
realities can be relevant depending on the research question. Reality is seen as 
dynamic and continuously shaped by human action. The question of whether reality 
is "really" objective or socially constructed is less important than whether a 
particular conception of reality helps solve the problem.

Epistemological Position (What Counts as Knowledge):
Knowledge is what allows us to act effectively in the world. Both quantitative 
and qualitative methods can generate valid knowledge, depending on the research 
question. Pragmatists reject the forced choice between positivism and interpretivism, 
arguing that different approaches are appropriate for different questions.

Axiological Position (Role of Values):
Research is driven by practical problems and the desire to improve situations. 
Values play a role in selecting research problems but should not bias interpretation 
of findings. The ultimate test of research is whether it produces useful outcomes.

Implications for Your Research:
- You may use mixed methods, combining quantitative and qualitative approaches
- Method choice is driven by research questions, not philosophical dogma
- You can integrate multiple data sources and perspectives
- Flexibility is valued over methodological purity
- Research aims to produce actionable, practical knowledge`,

        alignment: `This philosophy aligns well with:
- Mixed methods research designs
- Action research and applied research
- Evaluation research assessing program effectiveness
- Design-based research developing practical solutions
- Research with direct policy or practice implications`,

        citations: `Key Authors to Cite:
- Dewey, J. (1938) - Logic: The Theory of Inquiry
- Tashakkori, A., & Teddlie, C. (2010) - SAGE Handbook of Mixed Methods
- Creswell, J. W., & Plano Clark, V. L. (2017) - Designing and Conducting 
  Mixed Methods Research
- Morgan, D. L. (2014) - Pragmatism as a Paradigm for Social Research`
      },

      criticalRealism: {
        title: 'CRITICAL REALISM',
        description: `Critical realism is a philosophical approach developed primarily by Roy 
Bhaskar that attempts to bridge the divide between positivism and interpretivism. 
It acknowledges that an objective reality exists independently of our perceptions 
while recognizing that our knowledge of this reality is always mediated by 
historical, social, and cultural factors.

Ontological Position (Nature of Reality):
Critical realists adopt a stratified ontology, distinguishing between:
- The Empirical: What we observe and experience
- The Actual: Events that occur whether or not we observe them
- The Real: Underlying structures and mechanisms that generate events

Reality exists independently of our knowledge of it, but much of reality is 
"hidden"—we can only infer deep structures from their observable effects.

Epistemological Position (What Counts as Knowledge):
Knowledge is fallible and theory-laden but can progressively approximate reality. 
Scientific inquiry aims to identify the generative mechanisms that cause observed 
phenomena. This requires both empirical observation (like positivism) and 
interpretation of meaning (like interpretivism).

Axiological Position (Role of Values):
Research is value-relevant but strives for objectivity. Critical realists often 
focus on issues of power, inequality, and social justice, seeking to expose 
mechanisms of oppression while maintaining commitment to empirical investigation.

Implications for Your Research:
- You may use mixed methods to triangulate on underlying mechanisms
- Retroductive reasoning moves from observations to hypothesized mechanisms
- Research seeks explanatory understanding, not just description or prediction
- Context matters for understanding how mechanisms operate
- Theory development focuses on identifying causal powers and tendencies`,

        alignment: `This philosophy aligns well with:
- Case study research examining mechanisms in context
- Mixed methods combining measurement and meaning
- Realist evaluation of interventions and programs
- Research on complex social phenomena with multiple causes
- Studies aiming to explain "why" and "how" things work`,

        citations: `Key Authors to Cite:
- Bhaskar, R. (1975) - A Realist Theory of Science
- Sayer, A. (2000) - Realism and Social Science
- Pawson, R., & Tilley, N. (1997) - Realistic Evaluation
- Danermark, B. et al. (2002) - Explaining Society: Critical Realism in 
  the Social Sciences`
      },

      transformative: {
        title: 'TRANSFORMATIVE PARADIGM',
        description: `The transformative paradigm (also called critical theory, advocacy/participatory, 
or emancipatory research) explicitly foregrounds issues of power, social justice, 
and advocacy for marginalized groups. Research conducted from this paradigm 
aims not merely to understand the world but to change it—to challenge oppression 
and work toward more equitable social conditions.

Ontological Position (Nature of Reality):
Transformative researchers recognize that "reality" as commonly understood often 
reflects the perspectives of dominant groups. Multiple realities exist, shaped 
by social, political, economic, ethnic, cultural, gender, and disability values. 
Reality is historically situated and must be understood in context.

Epistemological Position (What Counts as Knowledge):
Knowledge is co-constructed between researchers and participants, with particular 
attention to whose knowledge has historically been silenced or marginalized. 
The relationship between researcher and participants is interactive, with 
participants viewed as experts on their own experience. Research should 
foreground the voices of those typically excluded from knowledge production.

Axiological Position (Role of Values):
Research is explicitly political and value-driven. Researchers make their values 
transparent and align their work with goals of social transformation. Neutrality 
is neither possible nor desirable; the choice to remain "neutral" often means 
accepting the status quo.

Implications for Your Research:
- Research questions address issues of power, oppression, or marginalization
- Participants are partners, not just data sources
- Methods may be adapted to be culturally appropriate and empowering
- Findings are used for advocacy and action
- Researchers practice reflexivity about their own positionality and privilege`,

        alignment: `This philosophy aligns well with:
- Participatory action research (PAR)
- Community-based participatory research (CBPR)
- Feminist research methodologies
- Indigenous research methodologies
- Disability studies and inclusive research`,

        citations: `Key Authors to Cite:
- Mertens, D. M. (2009) - Transformative Research and Evaluation
- Freire, P. (1970) - Pedagogy of the Oppressed
- hooks, b. (1994) - Teaching to Transgress
- Smith, L. T. (2012) - Decolonizing Methodologies`
      }
    };

    const selected = philosophyDetails[dataA.philosophy];
    if (selected) {
      section += `YOUR SELECTED PHILOSOPHY: ${selected.title}

${selected.description}

${selected.alignment}

${selected.citations}

`;
    } else {
      section += `No philosophical stance has been selected. Please complete Step 1 of 
Wizard A to specify your research philosophy.

`;
    }

    return section;
  };

  const generateTheoryApproachSection = () => {
    let section = `
================================================================================
SECTION 2: APPROACH TO THEORY DEVELOPMENT
================================================================================

The approach to theory development describes the logical relationship between 
theory and research. This decision fundamentally shapes whether you begin with 
existing theory and test it (deduction), begin with data and develop theory 
(induction), or move iteratively between theory and data (abduction).

`;

    const approachDetails = {
      deductive: {
        title: 'DEDUCTIVE APPROACH',
        description: `The deductive approach, sometimes called "top-down" reasoning, begins 
with existing theory and moves toward specific observations. This approach 
follows the classical scientific method: researchers start with a general 
theory, derive specific hypotheses from that theory, collect data to test 
the hypotheses, and either confirm or disconfirm the theoretical predictions.

The Deductive Process:
1. THEORY: Begin with an established theoretical framework
2. HYPOTHESES: Derive specific, testable predictions from the theory
3. OPERATIONALIZATION: Define how abstract concepts will be measured
4. DATA COLLECTION: Gather empirical data to test predictions
5. ANALYSIS: Statistically evaluate whether data support hypotheses
6. CONCLUSION: Confirm, refine, or reject the theory

Epistemological Foundations:
Deduction is closely associated with positivism and the natural science model. 
It assumes that reality operates according to discoverable laws and that 
theories should make falsifiable predictions. Karl Popper's philosophy of 
science emphasizes that good theories are those that could potentially be 
proven wrong (falsifiability).

When Deduction Is Appropriate:
- Substantial theoretical literature exists in your area
- You want to test predictions from an established theory
- Your research aims for generalization and prediction
- Quantitative methods are being used
- You have clear, measurable variables

Advantages:
- Builds on existing knowledge systematically
- Clear structure facilitates research design
- Enables hypothesis testing and statistical inference
- Results can confirm or challenge established theory

Limitations:
- May miss unexpected findings not predicted by theory
- Dependent on the quality of the starting theory
- May force data into predetermined categories
- Less suitable for exploring new phenomena`,

        implementation: `Implementation in Your Study:

You should:
1. Identify 1-3 key theories relevant to your research questions
2. Articulate specific hypotheses (H1, H2, H3, etc.) derived from theory
3. Specify your null hypotheses for statistical testing
4. Operationalize your variables before data collection
5. Use hypothesis-testing statistics (t-tests, ANOVA, regression, etc.)
6. Report whether each hypothesis was supported or not supported`
      },

      inductive: {
        title: 'INDUCTIVE APPROACH',
        description: `The inductive approach, sometimes called "bottom-up" reasoning, begins 
with specific observations and moves toward general theoretical conclusions. 
Rather than testing existing theory, inductive research generates new theory 
from patterns observed in empirical data. This approach is closely associated 
with qualitative research traditions.

The Inductive Process:
1. OBSERVATIONS: Collect rich, detailed data without predetermined categories
2. PATTERN IDENTIFICATION: Look for recurring themes, relationships, or structures
3. TENTATIVE PROPOSITIONS: Develop preliminary explanations for patterns
4. THEORETICAL FRAMEWORK: Build a coherent theory grounded in the data

Epistemological Foundations:
Induction is associated with interpretivism and the belief that social phenomena 
are too complex to be captured by pre-existing theories. Grounded theory, 
developed by Glaser and Strauss (1967), provides a systematic methodology 
for inductive theory building.

When Induction Is Appropriate:
- Little existing theory addresses your phenomenon
- You are exploring a new or under-researched area
- You want to understand meanings and perspectives
- Qualitative methods are being used
- Flexibility and openness to unexpected findings are valued

Advantages:
- Generates contextually grounded theory
- Open to surprising or unexpected findings
- Captures complexity and nuance
- Empowers participant perspectives

Limitations:
- Cannot confirm or test existing theories
- Generalization is limited
- Can be time-intensive
- Requires high tolerance for ambiguity`,

        implementation: `Implementation in Your Study:

You should:
1. Approach data collection with an open mind, not predetermined hypotheses
2. Use iterative data collection and analysis (constant comparison)
3. Develop codes and categories emerging from the data itself
4. Write memos documenting your thinking as theory develops
5. Continue until theoretical saturation (no new insights emerging)
6. Present your findings as a grounded theoretical framework`
      },

      abductive: {
        title: 'ABDUCTIVE APPROACH',
        description: `The abductive approach represents a middle path between deduction and 
induction, involving iterative movement between theory and data. Abduction 
begins when a researcher encounters a surprising or puzzling finding that 
existing theories cannot explain, then seeks the "best explanation" through 
a creative inferential process.

The Abductive Process:
1. PUZZLING OBSERVATION: Encounter an anomaly or surprising finding
2. THEORETICAL EXPLORATION: Consider possible explanatory frameworks
3. INFERENCE TO BEST EXPLANATION: Select the most plausible explanation
4. EMPIRICAL TESTING: Gather additional data to evaluate the explanation
5. THEORETICAL REFINEMENT: Modify theory based on new evidence
6. ITERATION: Continue cycling between theory and data

Epistemological Foundations:
Abduction was developed by philosopher Charles Sanders Peirce as the logic 
of discovery. Unlike deduction (which proves) or induction (which confirms), 
abduction generates new ideas. Critical realism often employs abductive 
reasoning to infer underlying mechanisms from observable effects.

When Abduction Is Appropriate:
- You have some theoretical foundation but also openness to emergence
- Your phenomenon involves complex, multi-causal processes
- You want to develop explanatory theory, not just describe
- Mixed methods approaches are being used
- You are building on but extending existing theory

Advantages:
- Balances theoretical grounding with empirical discovery
- Fosters creativity and theoretical innovation
- Suitable for complex, real-world phenomena
- Allows theory development and testing in one study

Limitations:
- Can be methodologically demanding
- Requires both theoretical knowledge and empirical skill
- May lack the clear structure of pure deduction or induction
- "Best explanation" is a subjective judgment`,

        implementation: `Implementation in Your Study:

You should:
1. Begin with a theoretical sensitization to your domain
2. Remain open to surprising or theory-challenging findings
3. When surprises occur, seek theoretical explanations
4. Collect additional data to evaluate alternative explanations
5. Document your reasoning process as you move between theory and data
6. Present findings as theoretical extension or refinement`
      }
    };

    const selected = approachDetails[dataA.theoryApproach];
    if (selected) {
      section += `YOUR SELECTED APPROACH: ${selected.title}

${selected.description}

${selected.implementation}

`;
    } else {
      section += `No approach to theory development has been selected. Please complete 
Step 2 of Wizard A to specify your approach.

`;
    }

    return section;
  };

  const generateMethodologicalChoiceSection = () => {
    let section = `
================================================================================
SECTION 3: METHODOLOGICAL CHOICE
================================================================================

Methodological choice refers to whether the study will employ quantitative 
methods (emphasizing numerical measurement), qualitative methods (emphasizing 
textual/visual data and meaning), or some combination of both. This decision 
has profound implications for research design, data collection, and analysis.

`;

    const methodDetails = {
      qualitative: {
        title: 'MONO METHOD: QUALITATIVE',
        description: `Qualitative research is an approach that emphasizes understanding 
social phenomena through exploration of meanings, experiences, and 
perspectives. Rather than reducing human experience to numbers, qualitative 
research seeks rich, contextual understanding through methods like interviews, 
observations, and document analysis.

Core Characteristics:
- NATURALISTIC: Studies phenomena in their natural settings
- INTERPRETIVE: Focuses on how participants make sense of their experiences
- HOLISTIC: Seeks comprehensive understanding of context
- EMERGENT: Research design evolves as understanding deepens
- REFLEXIVE: Researcher acknowledges their role in constructing findings

Philosophical Alignment:
Qualitative research is typically associated with interpretivism, constructionism, 
and phenomenological traditions. It assumes that social reality is constructed 
through human interaction and that understanding requires interpretation of 
meaning rather than measurement of variables.

Common Qualitative Approaches:
- Phenomenology: Understanding the essence of lived experience
- Grounded Theory: Developing theory from systematic data analysis
- Ethnography: Immersive study of cultural groups
- Case Study: In-depth exploration of bounded systems
- Narrative Inquiry: Analyzing stories and meaning-making

Quality Criteria:
Rather than validity and reliability (positivist terms), qualitative research 
uses criteria like:
- Credibility (internal validity equivalent)
- Transferability (external validity equivalent)
- Dependability (reliability equivalent)
- Confirmability (objectivity equivalent)

Data Collection Methods Typically Used:
- In-depth interviews (semi-structured or unstructured)
- Focus groups
- Participant observation
- Document and artifact analysis
- Visual methods (photographs, video)`
      },

      quantitative: {
        title: 'MONO METHOD: QUANTITATIVE',
        description: `Quantitative research is an approach that emphasizes objective 
measurement, numerical data, and statistical analysis. It seeks to identify 
patterns, test hypotheses, and establish generalizable findings through 
systematic data collection and mathematical analysis.

Core Characteristics:
- OBJECTIVE: Seeks researcher-independent findings
- MEASURABLE: Reduces concepts to quantifiable variables
- CONTROLLED: Attempts to isolate variables and control confounds
- GENERALIZABLE: Aims to extend findings beyond the sample
- REPLICABLE: Other researchers should obtain similar results

Philosophical Alignment:
Quantitative research is typically associated with positivism and post-positivism. 
It assumes that reality is objective and measurable, that causes can be 
identified through controlled observation, and that knowledge should be 
based on empirical evidence.

Common Quantitative Designs:
- Experimental: Manipulating variables to test causal effects
- Survey/Correlational: Measuring relationships between variables
- Longitudinal: Tracking changes over time
- Cross-sectional: Comparing groups at one time point

Quality Criteria:
Quantitative research is evaluated using:
- Internal validity: Did the IV cause changes in the DV?
- External validity: Can findings generalize to other contexts?
- Construct validity: Do measures capture intended constructs?
- Reliability: Are measurements consistent and stable?

Data Collection Methods Typically Used:
- Structured questionnaires/surveys
- Standardized tests and scales
- Experimental manipulation
- Systematic observation with coding schemes
- Secondary data from existing databases`
      },

      mixedMethods: {
        title: 'MIXED METHODS',
        description: `Mixed methods research intentionally integrates quantitative and 
qualitative approaches within a single study or program of research. This 
approach leverages the strengths of both traditions to provide more 
comprehensive understanding than either method alone could achieve.

Core Characteristics:
- INTEGRATION: Methods are combined, not just used side by side
- COMPLEMENTARITY: Each method addresses limitations of the other
- PRAGMATIC: Method choice driven by research questions
- COMPREHENSIVE: Multiple perspectives on the phenomenon

Philosophical Alignment:
Mixed methods is most commonly associated with pragmatism, which values 
"what works" over philosophical debates. Some researchers also use critical 
realism or dialectical frameworks to justify mixing methods.

Key Design Decisions:

Priority: ${dataA.mixedMethodsPriority || 'Not specified'}
${getMixedMethodsPriorityExplanation(dataA.mixedMethodsPriority)}

Timing: ${dataA.mixedMethodsTiming || 'Not specified'}
${getMixedMethodsTimingExplanation(dataA.mixedMethodsTiming)}

Integration: ${dataA.mixedMethodsIntegration || 'Not specified'}
${getMixedMethodsIntegrationExplanation(dataA.mixedMethodsIntegration)}

Common Mixed Methods Designs:
- Convergent Parallel: Collect both types simultaneously, compare results
- Explanatory Sequential: Quantitative first, then qualitative to explain
- Exploratory Sequential: Qualitative first, then quantitative to test
- Embedded: One type nested within a larger design of the other type

Quality Criteria:
Mixed methods quality includes:
- Design quality: Appropriateness of mixing approach
- Interpretive rigor: Quality of integration and meta-inferences
- Method-specific quality: Both components meet their respective standards`
      },

      multiMethod: {
        title: 'MULTI-METHOD',
        description: `Multi-method research uses multiple methods from the same paradigm 
(multiple qualitative methods or multiple quantitative methods) without 
crossing the qualitative-quantitative divide. This differs from mixed 
methods, which specifically integrates qualitative and quantitative approaches.

Examples:
- Multiple qualitative: Interviews AND focus groups AND observations
- Multiple quantitative: Survey AND experiment AND secondary data analysis

Advantages:
- Provides triangulation within a paradigm
- Addresses different facets of the research question
- Maintains philosophical consistency

When to Use:
Multi-method is appropriate when different methods within the same paradigm 
can complement each other, such as using observations to contextualize 
interview findings in qualitative research.`
      }
    };

    const selected = methodDetails[dataA.methodologicalChoice];
    if (selected) {
      section += `YOUR SELECTED METHODOLOGY: ${selected.title}

${selected.description}

`;
    } else {
      section += `No methodological choice has been specified. Please complete Step 3 
of Wizard A to specify your methodology.

`;
    }

    return section;
  };

  const getMixedMethodsPriorityExplanation = (priority) => {
    const explanations = {
      'QUAL priority': `Qualitative methods take priority. The quantitative component 
supplements and provides breadth to primarily qualitative findings.`,
      'QUANT priority': `Quantitative methods take priority. The qualitative component 
supplements and provides depth to primarily quantitative findings.`,
      'Equal priority': `Both methods have equal weight. Neither is subordinate to 
the other, and both contribute equally to conclusions.`
    };
    return explanations[priority] || '';
  };

  const getMixedMethodsTimingExplanation = (timing) => {
    const explanations = {
      'Sequential': `Methods are implemented in phases, with one method informing 
the design or interpretation of the subsequent method.`,
      'Concurrent': `Both methods are implemented simultaneously, with integration 
occurring during analysis or interpretation.`,
      'Multiphase': `Multiple sequential or concurrent strands are combined over 
an extended program of research.`
    };
    return explanations[timing] || '';
  };

  const getMixedMethodsIntegrationExplanation = (integration) => {
    const explanations = {
      'Merging/Convergent': `Quantitative and qualitative results are directly compared, 
contrasted, and synthesized to identify convergence, divergence, or complementarity.`,
      'Connecting': `Results from one method directly inform the design of the 
other method (e.g., qualitative interviews identifying variables for a survey).`,
      'Embedding': `One method is nested within a larger design primarily driven 
by the other method.`,
      'Mixed Methods Cases': `Mixed methods are applied within individual cases, 
with integration occurring at the case level.`
    };
    return explanations[integration] || '';
  };

  const generateStrategySection = () => {
    let section = `
================================================================================
SECTION 4: RESEARCH STRATEGY
================================================================================

Research strategy refers to the overall approach or design used to address 
the research questions. The strategy provides a general orientation for 
conducting the research, influencing decisions about sampling, data collection, 
and analysis.

`;

    const strategyDetails = {
      experiment: {
        title: 'EXPERIMENTAL DESIGN',
        description: `Experimental research is the gold standard for establishing causal 
relationships. It involves deliberate manipulation of an independent variable 
to observe its effect on a dependent variable, while controlling for confounding 
factors through random assignment and other procedures.

Key Elements:
- MANIPULATION: Researcher controls the independent variable
- CONTROL: Comparison group(s) that don't receive the manipulation
- RANDOMIZATION: Participants randomly assigned to conditions
- MEASUREMENT: Systematic assessment of dependent variable(s)

Types of Experimental Designs:
- True Experiment: Random assignment, control group, manipulation
- Quasi-Experiment: Manipulation but no random assignment
- Pre-Experiment: Single group, no control (weakest design)

Design Considerations:
Your design should address:
${formatStrategyDetails(dataA.strategyDetails)}

Threats to Validity:
- History: External events during the study
- Maturation: Natural changes in participants
- Testing effects: Practice or sensitization from pretests
- Attrition: Differential dropout between conditions
- Selection bias: Non-equivalent groups (if no randomization)`
      },

      survey: {
        title: 'SURVEY RESEARCH',
        description: `Survey research involves systematic collection of information from 
a sample of respondents using standardized questionnaires or interviews. 
Surveys are efficient for describing characteristics, attitudes, and behaviors 
of large populations.

Key Elements:
- STANDARDIZATION: All respondents receive the same questions
- SAMPLING: Selection of a subset from a defined population
- MEASUREMENT: Reliable and valid question design
- ANALYSIS: Statistical description and inference

Survey Modes:
- Online surveys (most common today)
- Paper-and-pencil questionnaires
- Telephone interviews (CATI)
- Face-to-face structured interviews

Design Considerations:
Your design should address:
${formatStrategyDetails(dataA.strategyDetails)}

Quality Issues:
- Sampling error: Difference between sample and population
- Coverage error: Population members who cannot be reached
- Nonresponse error: Bias from who chooses to respond
- Measurement error: Problems with question wording/format`
      },

      caseStudy: {
        title: 'CASE STUDY',
        description: `Case study research involves in-depth investigation of a contemporary 
phenomenon within its real-world context, especially when the boundaries 
between phenomenon and context are not clearly evident. Case studies are 
ideal for "how" and "why" research questions.

Key Elements:
- BOUNDED SYSTEM: Clear definition of the case being studied
- CONTEXT: Rich understanding of the setting and circumstances
- MULTIPLE SOURCES: Triangulation of data from various sources
- THICK DESCRIPTION: Detailed, contextualized account

Case Study Types:
- Single case: In-depth study of one case
- Multiple case: Comparative study across cases
- Intrinsic: Case is of interest in itself
- Instrumental: Case is used to understand a broader issue

Design Considerations:
Your design should address:
${formatStrategyDetails(dataA.strategyDetails)}

Quality Criteria (Yin, 2018):
- Construct validity: Use multiple sources of evidence
- Internal validity: Pattern matching, explanation building
- External validity: Theoretical generalization, not statistical
- Reliability: Case study protocol, database`
      },

      ethnography: {
        title: 'ETHNOGRAPHY',
        description: `Ethnography involves prolonged immersion in a cultural group or setting 
to understand shared patterns of behavior, beliefs, and language from an 
insider perspective. Originally developed in anthropology, ethnography is 
now used across social sciences.

Key Elements:
- IMMERSION: Extended time in the research setting
- PARTICIPANT OBSERVATION: Researcher participates while observing
- CULTURAL FOCUS: Understanding shared meanings and practices
- HOLISTIC: Comprehensive understanding of the culture

Types of Ethnography:
- Classical ethnography: Extended fieldwork (months/years)
- Focused ethnography: Shorter, more targeted studies
- Virtual ethnography: Study of online communities
- Autoethnography: Researcher's own cultural experience

Design Considerations:
Your design should address:
${formatStrategyDetails(dataA.strategyDetails)}

Quality Considerations:
- Prolonged engagement builds trust and deep understanding
- Triangulation across methods (observation, interviews, documents)
- Thick description allows readers to assess transferability
- Reflexivity about researcher's position and influence`
      },

      groundedTheory: {
        title: 'GROUNDED THEORY',
        description: `Grounded theory is a systematic methodology for developing theory 
that is "grounded" in data. Rather than beginning with a theory to test, 
grounded theory researchers collect and analyze data iteratively, allowing 
theory to emerge from the data itself.

Key Elements:
- ITERATIVE PROCESS: Data collection and analysis occur together
- CONSTANT COMPARISON: New data compared to emerging categories
- THEORETICAL SAMPLING: Sampling guided by developing theory
- THEORETICAL SATURATION: Data collection ends when no new insights emerge

Grounded Theory Approaches:
- Classic/Glaserian: More emergent, less structured
- Straussian: More systematic procedures (open, axial, selective coding)
- Constructivist (Charmaz): Acknowledges researcher's role in construction

Design Considerations:
Your design should address:
${formatStrategyDetails(dataA.strategyDetails)}

Key Procedures:
- Open coding: Initial labeling of data segments
- Axial coding: Identifying relationships between categories
- Selective coding: Integrating categories around a core category
- Memo writing: Documenting theoretical development`
      },

      phenomenology: {
        title: 'PHENOMENOLOGY',
        description: `Phenomenological research seeks to understand the essential structure 
or "essence" of a lived experience. It asks: What is it like to experience 
this phenomenon? Phenomenology brackets preconceptions to see the phenomenon 
as it presents itself.

Key Elements:
- LIVED EXPERIENCE: Focus on first-person experience
- EPOCHÉ/BRACKETING: Setting aside assumptions and judgments
- ESSENCE: Identifying core structures of experience
- RICH DESCRIPTION: Detailed account of the phenomenon

Phenomenological Approaches:
- Descriptive (Husserl): Focus on consciousness and description
- Interpretive/Hermeneutic (Heidegger): Emphasizes interpretation
- IPA (Smith): Idiographic, detailed analysis of individual cases

Design Considerations:
Your design should address:
${formatStrategyDetails(dataA.strategyDetails)}

Key Procedures:
- Epoché: Explicitly acknowledging preconceptions
- In-depth interviews exploring the experience
- Horizontalization: Treating all statements as equal initially
- Identifying invariant constituents and themes
- Writing structural and textural descriptions`
      },

      actionResearch: {
        title: 'ACTION RESEARCH',
        description: `Action research is a participatory, iterative approach that combines 
research with action to address practical problems while generating knowledge. 
Researchers and practitioners collaborate to identify problems, implement 
changes, and evaluate outcomes.

Key Elements:
- CYCLICAL PROCESS: Plan → Act → Observe → Reflect → Plan...
- PARTICIPATION: Stakeholders are partners in research
- ACTION ORIENTATION: Focus on solving real problems
- KNOWLEDGE AND CHANGE: Simultaneous goals

Action Research Types:
- Practical: Improving practice in a specific context
- Emancipatory/Critical: Addressing power and social justice
- Technical/Scientific: Systematic intervention testing

Design Considerations:
Your design should address:
${formatStrategyDetails(dataA.strategyDetails)}

Cycle Phases:
1. Diagnosing: Identifying the problem
2. Planning: Designing the intervention
3. Taking action: Implementing changes
4. Evaluating: Assessing outcomes
5. Specifying learning: Identifying insights for next cycle`
      },

      archival: {
        title: 'ARCHIVAL RESEARCH',
        description: `Archival research involves analysis of existing documents, records, 
or data that were created for purposes other than the current research. 
This includes historical documents, organizational records, media content, 
and secondary datasets.

Key Elements:
- EXISTING DATA: No primary data collection from participants
- HISTORICAL FOCUS: Often examines past events or trends
- DOCUMENT ANALYSIS: Systematic examination of records
- UNOBTRUSIVE: No researcher-participant interaction

Types of Archival Sources:
- Public records (government, legal, organizational)
- Mass media (newspapers, broadcasts, social media)
- Personal documents (letters, diaries, memoirs)
- Secondary datasets (census, surveys, administrative)

Design Considerations:
Your design should address:
${formatStrategyDetails(dataA.strategyDetails)}

Quality Considerations:
- Authenticity: Is the document genuine?
- Credibility: Is the content accurate?
- Representativeness: How typical is the document?
- Meaning: What was the document's original purpose?`
      }
    };

    const selected = strategyDetails[dataA.strategy];
    if (selected) {
      section += `YOUR SELECTED STRATEGY: ${selected.title}

${selected.description}

`;
    } else {
      section += `No research strategy has been specified. Please complete Step 4 
of Wizard A to specify your strategy.

`;
    }

    return section;
  };

  const formatStrategyDetails = (details) => {
    if (!details || Object.keys(details).length === 0) {
      return '(No specific details recorded)';
    }
    return Object.entries(details)
      .map(([key, value]) => `- ${key}: ${value}`)
      .join('\n');
  };

  const generateTimeHorizonSection = () => {
    let section = `
================================================================================
SECTION 5: TIME HORIZON
================================================================================

The time horizon refers to whether the research is a snapshot of a particular 
moment (cross-sectional) or tracks changes over time (longitudinal). This 
decision affects what conclusions can be drawn and the practical requirements 
of the study.

`;

    const horizonDetails = {
      crossSectional: {
        title: 'CROSS-SECTIONAL',
        description: `Cross-sectional research studies a phenomenon at a single point in 
time, like a "snapshot." This is the most common time horizon in social 
research because it is efficient and practical.

Characteristics:
- Data collected at one time point
- Examines variation across individuals, groups, or units
- Cannot directly observe change over time
- More efficient than longitudinal designs

Appropriate When:
- Research questions focus on describing current status
- You want to examine relationships between variables
- Time and resources are limited
- The phenomenon is relatively stable

Limitations:
- Cannot establish temporal precedence for causal inference
- Cannot track individual change trajectories
- Cohort effects may be confounded with age effects
- Recall bias if asking about past experiences

Your Design Details:
${formatStrategyDetails(dataA.timeHorizonDetails)}`
      },

      longitudinal: {
        title: 'LONGITUDINAL',
        description: `Longitudinal research studies phenomena over an extended period, 
collecting data at multiple time points. This design allows observation 
of change, development, and temporal ordering of variables.

Types of Longitudinal Designs:
- Panel/Cohort: Same individuals measured repeatedly
- Trend: Same population sampled at different times
- Retrospective: Looking back at past events
- Prospective: Following forward from baseline

Advantages:
- Can observe actual change over time
- Establishes temporal sequence (helps with causality)
- Identifies developmental patterns and trajectories
- Can distinguish age, period, and cohort effects

Challenges:
- More time-consuming and expensive
- Participant attrition over time
- Practice effects from repeated measurement
- Researcher and instrument changes over time

Your Design Details:
${formatStrategyDetails(dataA.timeHorizonDetails)}`
      }
    };

    const selected = horizonDetails[dataA.timeHorizon];
    if (selected) {
      section += `YOUR SELECTED TIME HORIZON: ${selected.title}

${selected.description}

`;
    } else {
      section += `No time horizon has been specified. Please complete Step 5 
of Wizard A to specify your time horizon.

`;
    }

    return section;
  };

  const generateTechniquesSection = () => {
    let section = `
================================================================================
SECTION 6: DATA COLLECTION TECHNIQUES AND PROCEDURES
================================================================================

This section details the specific methods for collecting data, the sampling strategy for selecting participants, and the analytical approaches for making sense of the data collected.

`;

    if (!dataA.techniques) {
      return section + `No techniques have been specified. Please complete Step 6 of Wizard A to specify your data collection methods.

`;
    }

    section += `--- DATA COLLECTION METHODS ---

`;

    if (dataA.techniques.dataCollection && dataA.techniques.dataCollection.length > 0) {
      const methodLabels = dataA.techniques.dataCollection.map(m => getDataCollectionLabel(m));
      section += `Selected Methods: ${methodLabels.join(', ')}

`;
      dataA.techniques.dataCollection.forEach(method => {
        section += getDataCollectionMethodDescription(method);
      });
    }

    const samplingStrategies = Array.isArray(dataA.techniques.sampling) 
      ? dataA.techniques.sampling 
      : (dataA.techniques.sampling ? [dataA.techniques.sampling] : []);
    
    const samplingLabels = samplingStrategies.map(s => getSamplingLabel(s));
    
    section += `
--- SAMPLING STRATEGY ---

Selected Strategy: ${samplingLabels.length > 0 ? samplingLabels.join(', ') : 'Not specified'}
Target Sample Size: ${getSampleSizeDisplay(true)}
`;

    const sampleSizeJustification = getSampleSizeJustification();
    if (sampleSizeJustification) {
      section += `
Sample Size Justification:
${sampleSizeJustification}
`;
    }

    if (dataA.techniques.sampleSizesPerMethod && Object.keys(dataA.techniques.sampleSizesPerMethod).length > 0) {
      section += `
Sample Sizes by Method:
`;
      Object.entries(dataA.techniques.sampleSizesPerMethod).forEach(([method, size]) => {
        if (size && parseInt(size) > 0) {
          section += `  - ${getDataCollectionLabel(method)}: N = ${size}
`;
        }
      });
    }
    section += `
`;

    samplingStrategies.forEach(strategy => {
      section += getSamplingStrategyDescription(strategy);
    });

    section += `
--- ANALYSIS METHODS ---

`;

    if (dataA.techniques.analysis && dataA.techniques.analysis.length > 0) {
      const analysisLabels = dataA.techniques.analysis.map(m => getAnalysisLabel(m));
      section += `Selected Methods: ${analysisLabels.join(', ')}

`;
      dataA.techniques.analysis.forEach(method => {
        section += getAnalysisMethodDescription(method);
      });
    }

    section += `
--- QUALITY CRITERIA ---

`;

    if (dataA.techniques.qualityCriteria && dataA.techniques.qualityCriteria.length > 0) {
      const criteriaLabels = dataA.techniques.qualityCriteria.map(c => getQualityCriteriaLabel(c));
      section += `Selected Criteria: ${criteriaLabels.join(', ')}

Ensuring quality in research requires attention to multiple criteria throughout the research process:

`;
      dataA.techniques.qualityCriteria.forEach(criterion => {
        section += getQualityCriterionDescription(criterion);
      });
    }

    if (dataA.techniques.qualAnalysisJustification) {
      section += `
--- JUSTIFICATION FOR MULTIPLE QUALITATIVE ANALYSIS METHODS ---

${dataA.techniques.qualAnalysisJustification}

`;
    }

    if (dataA.techniques.quantAnalysisJustification) {
      section += `
--- JUSTIFICATION FOR MULTIPLE QUANTITATIVE ANALYSIS METHODS ---

${dataA.techniques.quantAnalysisJustification}

`;
    }

    return section;
  };

  const getDataCollectionMethodDescription = (method) => {
    const label = getDataCollectionLabel(method).toUpperCase();
    const descriptions = {
      interviews: `${label}
In-depth interviews allow researchers to explore participants' perspectives, experiences, and meanings in detail. Semi-structured interviews balance flexibility with consistency across participants.

`,
      semiStructuredInt: `SEMI-STRUCTURED INTERVIEWS
Semi-structured interviews use a flexible interview guide with open-ended questions while allowing the conversation to flow naturally. This approach balances consistency across participants with the freedom to explore emerging topics and probe deeper into responses.

`,
      structuredInt: `STRUCTURED INTERVIEWS
Structured interviews use standardized questions asked in a fixed order with predetermined response options. This format ensures consistency across all participants and facilitates comparison of responses, making it suitable when specific information needs to be gathered systematically.

`,
      focusGroups: `FOCUS GROUPS
Focus groups leverage group interaction to explore shared meanings and generate data that might not emerge in individual interviews. Ideal for understanding social norms and collective perspectives.

`,
      surveys: `SURVEYS/QUESTIONNAIRES
Standardized questionnaires efficiently collect data from large samples. Careful question design is essential for validity. Consider using validated scales where available.

`,
      structuredSurvey: `STRUCTURED SURVEYS/QUESTIONNAIRES
Structured surveys use standardized instruments with fixed response options to collect quantifiable data from participants. This approach ensures consistency across respondents and enables statistical analysis of the collected data.

`,
      observation: `OBSERVATION
Observation allows direct study of behavior in natural settings. Can be participant (researcher joins activities) or non-participant (researcher watches from outside).

`,
      structuredObs: `STRUCTURED OBSERVATION
Structured observation uses a predetermined coding scheme to systematically record specific behaviors or events. This method allows for quantifiable data collection while observing participants in their natural environment.

`,
      participantObs: `PARTICIPANT OBSERVATION
Participant observation involves the researcher immersing themselves in the research setting, participating in activities while observing. This ethnographic method provides deep insight into social contexts, practices, and meanings from an insider perspective.

`,
      documents: `DOCUMENT ANALYSIS
Analysis of existing documents (policies, records, media) provides insight without requiring participant involvement. Important to assess authenticity and purpose of documents.

`,
      secondaryData: `SECONDARY/ADMINISTRATIVE DATA
Using existing datasets is efficient but limits control over data quality and variables measured. Ensure data is appropriate for your research questions. This includes administrative records, government data, and previously collected research data.

`,
      experiments: `EXPERIMENTAL PROCEDURES
Experimental procedures involve controlled manipulation of one or more variables to observe their effect on outcomes. Random assignment and control groups help establish causal relationships between variables.

`,
      tests: `STANDARDIZED TESTS/SCALES
Standardized tests and validated measurement scales provide reliable and valid assessment of specific constructs. Using established instruments with known psychometric properties strengthens the credibility of research findings.

`,
      fieldNotes: `FIELD NOTES/JOURNALS
Field notes are the researcher's reflective records documenting observations, thoughts, and interpretations during fieldwork. These records capture contextual details and emerging insights that inform the analysis.

`,
      visualMethods: `VISUAL METHODS
Visual methods include the use of photographs, videos, drawings, and diagrams as data sources or analytical tools. These approaches can capture aspects of experience that may be difficult to express verbally.

`
    };
    return descriptions[method] || `${label}
This data collection method was selected for your research design.

`;
  };

  const getSamplingStrategyDescription = (strategy) => {
    const label = getSamplingLabel(strategy).toUpperCase();
    const descriptions = {
      simpleRandom: `SIMPLE RANDOM SAMPLING
Every member of the population has an equal probability of selection. This probability sampling approach enables statistical generalization but requires a complete sampling frame.

`,
      systematic: `SYSTEMATIC SAMPLING
Participants are selected at regular intervals from an ordered list (e.g., every 10th person). This approach is simpler than simple random sampling while still providing a probability-based sample.

`,
      stratified: `STRATIFIED SAMPLING
The population is divided into subgroups (strata) and random samples are drawn from each stratum. This ensures representation of key subgroups and can increase precision.

`,
      cluster: `CLUSTER SAMPLING
Naturally occurring groups (clusters) are randomly selected, and all members of selected clusters are studied. Practical for geographically dispersed populations.

`,
      multistage: `MULTISTAGE SAMPLING
Combines cluster sampling with random sampling at different stages. First, clusters are randomly selected, then individuals within those clusters are randomly sampled.

`,
      pps: `PROBABILITY PROPORTIONAL TO SIZE (PPS)
Larger clusters or units have a higher probability of selection, proportional to their size. This approach is particularly useful when cluster sizes vary substantially.

`,
      convenience: `CONVENIENCE SAMPLING
Participants are selected based on availability and accessibility. While practical, this limits generalizability. Common in pilot studies and exploratory research.

`,
      purposive: `PURPOSIVE (JUDGMENTAL) SAMPLING
Participants are deliberately selected based on specific characteristics relevant to the research questions. Common in qualitative research where depth is prioritized over breadth.

`,
      quota: `QUOTA SAMPLING
The researcher identifies quotas for specific subgroups but samples non-randomly within each quota. This ensures representation of key groups without requiring probability-based selection.

`,
      snowball: `SNOWBALL SAMPLING
Initial participants recruit additional participants from their networks. Useful for hard-to-reach populations but may limit diversity.

`,
      volunteer: `VOLUNTEER/SELF-SELECTION SAMPLING
Participants choose to participate in the study voluntarily. This approach is practical for recruiting but may introduce self-selection bias.

`,
      expert: `EXPERT SAMPLING
Participants are selected based on their recognized expertise in a specific field or topic. This approach is valuable when specialized knowledge is essential to the research.

`,
      theoretical: `THEORETICAL SAMPLING
Sampling decisions are guided by emerging theory, seeking participants who can contribute to theoretical development. Central to grounded theory research.

`,
      homogeneous: `HOMOGENEOUS SAMPLING
Participants are selected because they share similar characteristics or experiences. This approach is useful when the research focuses on a specific subgroup or phenomenon.

`,
      maxVariation: `MAXIMUM VARIATION SAMPLING
Participants are deliberately selected to capture a wide range of perspectives and experiences. This approach helps identify common patterns across diverse cases.

`,
      criticalCase: `CRITICAL CASE SAMPLING
Cases are selected based on their special significance or ability to illuminate the research question. A single critical case can provide crucial insights.

`,
      sequential: `SEQUENTIAL SAMPLING
Sample selection occurs in phases, with each phase informed by the results of the previous phase. This iterative approach allows the sample to evolve based on emerging findings.

`,
      adaptive: `ADAPTIVE SAMPLING
The sampling design changes during data collection when rare or important cases are discovered. This flexible approach is useful when studying rare phenomena.

`,
      rds: `RESPONDENT-DRIVEN SAMPLING (RDS)
An advanced form of snowball sampling that uses statistical weighting to account for network structure. This approach can produce more representative samples of hidden populations.

`,
      panel: `PANEL SAMPLING
The same sample of participants is studied repeatedly over time. This longitudinal approach allows tracking of changes within individuals.

`
    };
    return descriptions[strategy] || `${label}
This sampling strategy was selected for your research design.

`;
  };

  const getAnalysisMethodDescription = (method) => {
    const label = getAnalysisLabel(method).toUpperCase();
    const descriptions = {
      thematicAnalysis: `THEMATIC ANALYSIS
Identifies, analyzes, and reports patterns (themes) within the data. Flexible and accessible, suitable for various epistemological positions. Follow Braun & Clarke's (2006) six-phase approach.

`,
      contentAnalysis: `CONTENT ANALYSIS
Systematic categorization and quantification of textual or visual data. Can be quantitative (counting occurrences) or qualitative (interpreting meanings).

`,
      groundedTheoryCoding: `GROUNDED THEORY CODING
Systematic coding process (open, axial, selective) to develop theory from data. Involves constant comparison and theoretical memo writing.

`,
      narrativeAnalysis: `NARRATIVE ANALYSIS
Focuses on the stories people tell, examining structure, content, and performance of narratives. Attends to how experiences are storied.

`,
      discourseAnalysis: `DISCOURSE ANALYSIS
Examines how language constructs social reality. Various approaches exist, from conversation analysis to critical discourse analysis.

`,
      phenomenologicalAnalysis: `PHENOMENOLOGICAL ANALYSIS
Focuses on describing and understanding the essence of lived experiences. Seeks to uncover the meaning structures of particular phenomena as experienced by participants.

`,
      frameworkAnalysis: `FRAMEWORK ANALYSIS
A matrix-based approach that organizes data into themes and cases. Particularly useful for applied policy research and when working with pre-defined research questions.

`,
      ipa: `INTERPRETATIVE PHENOMENOLOGICAL ANALYSIS (IPA)
Focuses on understanding how individuals make sense of their personal and social world. Combines phenomenological and interpretive perspectives.

`,
      descriptiveStats: `DESCRIPTIVE STATISTICS
Summarizes data using measures of central tendency (mean, median, mode), dispersion (standard deviation, range), and distribution shape. Essential foundation for all quantitative analysis.

`,
      inferentialStats: `INFERENTIAL STATISTICS
Uses sample data to make inferences about populations. Includes hypothesis testing, confidence intervals, and statistical significance assessment.

`,
      regression: `REGRESSION ANALYSIS
Models the relationship between predictor variables and an outcome. Multiple regression assesses unique contributions of each predictor while controlling for other variables.

`,
      anova: `ANALYSIS OF VARIANCE (ANOVA/ANCOVA)
Compares means across three or more groups. Various forms exist: one-way, factorial, repeated measures, ANCOVA (with covariates), and MANOVA (multiple outcomes).

`,
      factorAnalysis: `FACTOR ANALYSIS
Identifies underlying latent variables (factors) that explain correlations among observed variables. Used for scale development and validation.

`,
      sem: `STRUCTURAL EQUATION MODELING (SEM)
Comprehensive approach combining factor analysis and path analysis to test complex theoretical models with latent variables.

`,
      timeSeries: `TIME SERIES ANALYSIS
Analyzes data collected over time to identify patterns, trends, and seasonal variations. Useful for forecasting and understanding temporal dynamics.

`
    };
    return descriptions[method] || `${label}
This analysis method was selected for your research design.

`;
  };

  const getQualityCriterionDescription = (criterion) => {
    const label = getQualityCriteriaLabel(criterion).toUpperCase();
    const descriptions = {
      validity: `VALIDITY
The extent to which the research accurately measures what it claims to measure and the conclusions are justified by the evidence.

`,
      internalValidity: `INTERNAL VALIDITY
The degree to which a study can establish a causal relationship between variables, ruling out alternative explanations for observed effects.

`,
      externalValidity: `EXTERNAL VALIDITY
The extent to which research findings can be generalized to other settings, populations, or time periods beyond the study context.

`,
      constructValidity: `CONSTRUCT VALIDITY
The degree to which a test or instrument measures the theoretical construct it is intended to measure.

`,
      statConclusion: `STATISTICAL CONCLUSION VALIDITY
The appropriateness of statistical inferences made from the data, including adequate power and proper use of statistical tests.

`,
      reliability: `RELIABILITY
The consistency and stability of measurements or observations across time, raters, or instruments.

`,
      credibility: `CREDIBILITY
The qualitative equivalent of internal validity. Establishes that findings are believable from participants' perspective.

`,
      transferability: `TRANSFERABILITY
The qualitative equivalent of external validity. Provides sufficient detail for readers to judge applicability to other contexts.

`,
      dependability: `DEPENDABILITY
The qualitative equivalent of reliability. Achieved through an audit trail documenting research decisions.

`,
      confirmability: `CONFIRMABILITY
The qualitative equivalent of objectivity. Ensures findings are shaped by participants, not researcher bias.

`,
      authenticity: `AUTHENTICITY
Fair and balanced representation of all participants' perspectives, ensuring diverse viewpoints are represented in the findings.

`,
      resonance: `RESONANCE
The degree to which findings ring true to participants and readers, creating a sense of recognition and understanding.

`,
      triangulation: `TRIANGULATION
Using multiple sources, methods, or perspectives to corroborate findings and reduce systematic bias.

`,
      memberChecking: `MEMBER CHECKING
Returning findings to participants for verification. Enhances credibility and respects participant voice.

`,
      reflexivity: `REFLEXIVITY
Ongoing critical self-reflection about the researcher's role, biases, and influence on the research process and findings.

`
    };
    return descriptions[criterion] || `${label}
This quality criterion was selected for your research design.

`;
  };

  const generateCoherenceSection = () => {
    return `
================================================================================
SECTION 7: METHODOLOGICAL COHERENCE
================================================================================

A rigorous research design demonstrates coherence - logical alignment between philosophy, methodology, and methods. Your design should form an internally consistent framework where each decision supports the others.

Coherence Check for Your Design:

Philosophy → Theory Approach:
${checkCoherence('philTheory')}

Philosophy → Methodology:
${checkCoherence('philMethod')}

Methodology → Strategy:
${checkCoherence('methodStrategy')}

Strategy → Time Horizon:
${checkCoherence('strategyTime')}

Overall Assessment:
${getOverallCoherenceAssessment()}

`;
  };

  const checkCoherence = (type) => {
    const coherenceMessages = {
      philTheory: () => {
        if (dataA.philosophy === 'positivism' && dataA.theoryApproach === 'deductive') {
          return '✓ Strong alignment. Positivism and deduction both emphasize hypothesis testing.';
        }
        if (dataA.philosophy === 'interpretivism' && dataA.theoryApproach === 'inductive') {
          return '✓ Strong alignment. Interpretivism and induction both prioritize emergent understanding.';
        }
        if (dataA.philosophy === 'pragmatism') {
          return '✓ Pragmatism accommodates various theory approaches based on research needs.';
        }
        if (dataA.philosophy === 'criticalRealism' && dataA.theoryApproach === 'abductive') {
          return '✓ Strong alignment. Critical realism often employs abductive reasoning.';
        }
        return '⚠ Consider whether your philosophy and theory approach are aligned.';
      },
      philMethod: () => {
        if (dataA.philosophy === 'positivism' && dataA.methodologicalChoice === 'quantitative') {
          return '✓ Strong alignment. Positivism emphasizes measurable, quantifiable knowledge.';
        }
        if (dataA.philosophy === 'interpretivism' && dataA.methodologicalChoice === 'qualitative') {
          return '✓ Strong alignment. Interpretivism emphasizes meaning and interpretation.';
        }
        if (dataA.philosophy === 'pragmatism' && dataA.methodologicalChoice === 'mixedMethods') {
          return '✓ Strong alignment. Pragmatism supports mixing methods as needed.';
        }
        if (dataA.philosophy === 'criticalRealism' && dataA.methodologicalChoice === 'mixedMethods') {
          return '✓ Alignment. Critical realism often benefits from multiple methods.';
        }
        return '⚠ Consider whether your philosophy supports your methodological choice.';
      },
      methodStrategy: () => {
        if (dataA.methodologicalChoice === 'quantitative' && 
            ['experiment', 'survey'].includes(dataA.strategy)) {
          return '✓ Strong alignment. Experiments and surveys are core quantitative strategies.';
        }
        if (dataA.methodologicalChoice === 'qualitative' && 
            ['ethnography', 'groundedTheory', 'phenomenology', 'caseStudy'].includes(dataA.strategy)) {
          return '✓ Strong alignment. This strategy is appropriate for qualitative research.';
        }
        if (dataA.methodologicalChoice === 'mixedMethods' && dataA.strategy === 'caseStudy') {
          return '✓ Case studies often incorporate mixed methods effectively.';
        }
        return '⚠ Consider whether your strategy aligns with your methodology.';
      },
      strategyTime: () => {
        if (dataA.strategy === 'experiment' && dataA.timeHorizon === 'longitudinal') {
          return '✓ Longitudinal experiments can track effects over time.';
        }
        if (dataA.strategy === 'ethnography' && dataA.timeHorizon === 'longitudinal') {
          return '✓ Ethnography typically requires extended engagement over time.';
        }
        if (dataA.strategy === 'survey' && dataA.timeHorizon === 'crossSectional') {
          return '✓ Cross-sectional surveys are efficient for describing populations.';
        }
        return '⚠ Consider whether your time horizon suits your strategy.';
      }
    };
    return coherenceMessages[type] ? coherenceMessages[type]() : '';
  };

  const getOverallCoherenceAssessment = () => {
    return `Your methodological choices should form an integrated framework. Review any warnings above and consider whether adjustments might strengthen alignment. Remember that justified departures from typical alignments are acceptable when well-reasoned and explained in your methodology chapter.`;
  };

  const generateLimitationsSection = () => {
    return `
================================================================================
SECTION 8: ANTICIPATED LIMITATIONS
================================================================================

All research designs have limitations. Acknowledging these demonstrates methodological awareness and helps readers appropriately contextualize findings.

Common Limitations by Design Choice:

${getPhilosophyLimitations()}
${getMethodologyLimitations()}
${getStrategyLimitations()}
${getTimeHorizonLimitations()}
${getSamplingLimitations()}

Addressing Limitations:
When writing your methodology chapter, acknowledge relevant limitations and explain how you have mitigated them where possible. Frame limitations honestly but avoid undermining your entire design.

`;
  };

  const getPhilosophyLimitations = () => {
    const limitations = {
      positivism: `Philosophy (Positivism):
- May oversimplify complex social phenomena
- Assumes social world operates like natural world
- Limited ability to capture meaning and context`,
      interpretivism: `Philosophy (Interpretivism):
- Findings may be highly context-dependent
- Researcher interpretation introduces subjectivity
- Generalization to other contexts is limited`,
      pragmatism: `Philosophy (Pragmatism):
- May lack clear philosophical foundation
- Mixing methods requires expertise in both
- Integration of findings can be challenging`,
      criticalRealism: `Philosophy (Critical Realism):
- Underlying mechanisms are inferred, not directly observed
- Requires sophisticated theoretical reasoning
- Less established procedures than other paradigms`,
      transformative: `Philosophy (Transformative):
- Explicit political stance may affect credibility with some audiences
- Advocacy and objectivity may conflict
- May prioritize empowerment over traditional rigor criteria`
    };
    return limitations[dataA.philosophy] || '';
  };

  const getMethodologyLimitations = () => {
    const limitations = {
      qualitative: `Methodology (Qualitative):
- Small samples limit generalizability
- Researcher influence on data collection and interpretation
- Time-intensive data collection and analysis`,
      quantitative: `Methodology (Quantitative):
- May miss nuances and context
- Reliance on valid measurement instruments
- Statistical significance ≠ practical significance`,
      mixedMethods: `Methodology (Mixed Methods):
- Requires expertise in both approaches
- Integration is challenging
- More time and resources required`,
      multiMethod: `Methodology (Multi-Method):
- Complexity of multiple data sources
- Consistency across methods may be challenging`
    };
    return limitations[dataA.methodologicalChoice] || '';
  };

  const getStrategyLimitations = () => {
    const limitations = {
      experiment: `Strategy (Experimental):
- Artificial laboratory conditions may reduce ecological validity
- Ethical constraints limit what can be manipulated
- Participant awareness may affect behavior (demand characteristics)`,
      survey: `Strategy (Survey):
- Self-report bias and social desirability
- Low response rates threaten representativeness
- Cannot establish causation`,
      caseStudy: `Strategy (Case Study):
- Limited generalizability beyond the case
- Researcher bias in case selection and interpretation
- Defining case boundaries can be challenging`,
      ethnography: `Strategy (Ethnography):
- Very time-intensive
- Researcher presence may alter behavior
- Access and rapport building challenges`,
      groundedTheory: `Strategy (Grounded Theory):
- Difficult to completely bracket preconceptions
- Reaching saturation is subjective
- Theory may be too context-specific`,
      phenomenology: `Strategy (Phenomenology):
- Relies heavily on participants' ability to articulate experience
- Bracketing preconceptions is difficult
- Essence may be elusive or contested`
    };
    return limitations[dataA.strategy] || '';
  };

  const getTimeHorizonLimitations = () => {
    const limitations = {
      crossSectional: `Time Horizon (Cross-Sectional):
- Cannot establish temporal sequence
- Cannot track actual change over time
- Cohort and age effects confounded`,
      longitudinal: `Time Horizon (Longitudinal):
- Participant attrition over time
- Practice effects from repeated measurement
- Resource-intensive`
    };
    return limitations[dataA.timeHorizon] || '';
  };

  const getSamplingLimitations = () => {
    const samplingRaw = dataA.techniques?.sampling;
    const samplingArray = Array.isArray(samplingRaw) ? samplingRaw : (samplingRaw ? [samplingRaw] : []);
    
    const limitations = {
      simpleRandom: `Sampling (Simple Random):
- Requires complete sampling frame
- May not capture subgroup differences
- Can be expensive for large populations`,
      convenience: `Sampling (Convenience):
- Non-representative sample
- Limited generalizability
- Self-selection bias`,
      purposive: `Sampling (Purposive):
- Researcher bias in selection
- Limited generalizability
- May miss important perspectives`,
      snowball: `Sampling (Snowball):
- Network bias (similar participants)
- Limited diversity
- Cannot calculate response rates`,
      quota: `Sampling (Quota):
- Non-random selection within quotas
- May not be statistically representative
- Researcher bias in participant selection`,
      stratified: `Sampling (Stratified):
- Requires knowledge of population strata
- More complex than simple random sampling
- May be costly to implement`,
      cluster: `Sampling (Cluster):
- Higher sampling error than simple random
- Intra-cluster homogeneity reduces precision
- Complex statistical analysis required`,
      theoretical: `Sampling (Theoretical):
- Depends on researcher judgment
- May be difficult to replicate
- Saturation is subjective`
    };
    
    const results = samplingArray.map(s => limitations[s]).filter(Boolean);
    return results.join('\n\n');
  };

  const generateFullReport = () => {
    let fullReport = partAReport;
    
    if (wizardBComplete && dataB.analysisPlan) {
      fullReport += `\n\n`;
      fullReport += dataB.analysisPlan;
    }

    fullReport += `
================================================================================
DECISION LOG & JUSTIFICATIONS
================================================================================

`;

    decisions.forEach((decision, idx) => {
      fullReport += `${idx + 1}. [Wizard ${decision.wizard}] ${decision.step}
   Decision: ${decision.decision}
   Rationale: ${decision.rationale}
   Timestamp: ${new Date(decision.timestamp).toLocaleString()}

`;
    });

    fullReport += `
================================================================================
                        END OF COMPLETE REPORT
================================================================================
`;

    return fullReport;
  };

  if (!canAccessReport) {
    return (
      <div className="wizard-step final-report">
        <h2>Report Generation</h2>
        
        <div className="report-locked-notice">
          <h3><Lock size={24} /> Report Access Locked</h3>
          <p>
            Your research design progress has been saved, but generating and downloading reports 
            requires an upgraded subscription plan.
          </p>
          <button 
            className="upgrade-btn-inline"
            onClick={() => triggerUpgradeModal(`report${reportKey}`)}
          >
            <Crown size={18} />
            Upgrade to Generate Reports
          </button>
        </div>

        <div className="completion-summary">
          <div className="completion-card">
            <FileText size={48} />
            <h3>Your Progress So Far</h3>
            <p>You've completed the wizard steps. Upgrade to unlock full reports with:</p>
            <ul className="summary-list">
              <li>Downloadable methodology documentation</li>
              <li>Copy-to-clipboard functionality</li>
              <li>Print-ready formatting</li>
              <li>Combined reports across wizards</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-step final-report">
      <h2>Congratulations! Your Research Design is Complete</h2>
      
      <div className="completion-summary">
        <div className="completion-card">
          <FileText size={48} />
          <h3>Wizard A: Research Methodology</h3>
          <p className="status">{wizardAComplete ? '✓ Complete' : '⚠ Incomplete'}</p>
          {wizardAComplete && (
            <ul className="summary-list">
              {dataA.philosophicalDimensions?.ontology && (
                <li>Ontology: {getDimensionLabel('ontology', dataA.philosophicalDimensions.ontology)}</li>
              )}
              {dataA.philosophicalDimensions?.epistemology && (
                <li>Epistemology: {getDimensionLabel('epistemology', dataA.philosophicalDimensions.epistemology)}</li>
              )}
              {dataA.philosophicalDimensions?.axiology && (
                <li>Axiology: {getDimensionLabel('axiology', dataA.philosophicalDimensions.axiology)}</li>
              )}
              <li>Paradigm: {getParadigmLabel(dataA.philosophy)}</li>
              <li>Approach: {dataA.theoryApproach}</li>
              <li>Method: {dataA.methodologicalChoice}</li>
              <li>Strategy: {dataA.strategy}</li>
              <li>Time Horizon: {dataA.timeHorizon}</li>
            </ul>
          )}
          {!wizardAComplete && (
            <button className="btn btn-primary" onClick={() => switchWizard('A')}>
              Complete Wizard A
            </button>
          )}
        </div>

        <div className="completion-card">
          <FileText size={48} />
          <h3>Wizard B: Statistical Analysis</h3>
          <p className="status">{wizardBComplete ? '✓ Complete' : '⚠ Not Started'}</p>
          {wizardBComplete && (
            <ul className="summary-list">
              <li>Questions: {Array.isArray(dataB.questionFamilies) ? dataB.questionFamilies.join(', ') : dataB.questionFamily}</li>
              <li>DV: {dataB.variables?.dvType}</li>
              <li>IV: {dataB.variables?.ivTypes?.join(', ') || dataB.variables?.ivType}</li>
              <li>N: {dataB.variables?.sampleSize}</li>
              <li>Test: {dataB.recommendations?.primaryTest}</li>
            </ul>
          )}
          {!wizardBComplete && wizardAComplete && (
            <button className="btn btn-primary" onClick={() => switchWizard('B')}>
              Start Wizard B
            </button>
          )}
        </div>
      </div>

      {wizardAComplete && (
        <>
          <div className="diagram-toggle">
            <button 
              className={diagramView === 'simple' ? 'active' : ''} 
              onClick={() => setDiagramView('simple')}
            >
              Linear Research Framework
            </button>
            <button 
              className={diagramView === 'decision' ? 'active' : ''} 
              onClick={() => setDiagramView('decision')}
            >
              Decision Flow Framework
            </button>
          </div>
          
          {diagramView === 'simple' ? (
            <ResearchDesignDiagram 
              dataA={dataA} 
              dataB={dataB} 
              wizardAComplete={wizardAComplete} 
              wizardBComplete={wizardBComplete} 
            />
          ) : (
            <ResearchDecisionFlow 
              dataA={dataA} 
              dataB={dataB} 
              wizardAComplete={wizardAComplete} 
              wizardBComplete={wizardBComplete} 
            />
          )}
        </>
      )}

      {wizardAComplete && (
        <div className="report-preview-section">
          <div className="report-header" onClick={() => setShowPartAPreview(!showPartAPreview)}>
            <h3>Part A: Research Methodology Report</h3>
            <div className="report-header-actions">
              {showPartAPreview ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
          
          {showPartAPreview && (
            <>
              <div className="plan-actions">
                <button className="btn btn-secondary" onClick={handleCopyPartA}>
                  <Copy size={18} />
                  {copySuccessA ? 'Copied!' : 'Copy to Clipboard'}
                </button>
                <button className="btn btn-secondary" onClick={handleDownloadPartA}>
                  <Download size={18} />
                  Download Part A
                </button>
              </div>

              <div className="report-html-container">
                {renderReportAsHTML(partAReport)}
              </div>
            </>
          )}
        </div>
      )}

      {wizardBComplete && dataB.analysisPlan && (
        <div className="report-preview-section">
          <div className="report-header" onClick={() => setShowPartBPreview(!showPartBPreview)}>
            <h3>Part B: Statistical Analysis Plan</h3>
            <div className="report-header-actions">
              {showPartBPreview ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
          
          {showPartBPreview && (
            <>
              <div className="plan-actions">
                <button className="btn btn-secondary" onClick={handleCopyPartB}>
                  <Copy size={18} />
                  {copySuccessB ? 'Copied!' : 'Copy to Clipboard'}
                </button>
                <button className="btn btn-secondary" onClick={handleDownloadPartB}>
                  <Download size={18} />
                  Download Part B
                </button>
              </div>

              <div className="report-html-container">
                {renderReportAsHTML(dataB.analysisPlan)}
              </div>
            </>
          )}
        </div>
      )}

      <div className="action-buttons">
        <button className="btn btn-primary btn-large" onClick={handleDownloadFull}>
          <Download size={20} />
          Download Complete Report (Parts A + B)
        </button>

        <button className="btn btn-secondary" onClick={() => window.print()}>
          <Printer size={20} />
          Print Report
        </button>
        
        <button className="btn btn-secondary" onClick={resetWizard}>
          <RotateCcw size={20} />
          Start New Design
        </button>

        {wizardAComplete && !wizardBComplete && (
          <button className="btn btn-accent" onClick={() => switchWizard('B')}>
            <FileText size={20} />
            Continue to Statistical Analysis (Wizard B)
          </button>
        )}
      </div>

      <div className="next-steps-section">
        <h3>Next Steps for Your Research</h3>
        <div className="next-steps-grid">
          <div className="next-step-card">
            <h4>1. Refine Your Design</h4>
            <ul>
              <li>Consult with your supervisor/advisor</li>
              <li>Review relevant methodological literature</li>
              <li>Consider pilot testing your instruments</li>
            </ul>
          </div>
          <div className="next-step-card">
            <h4>2. Ethics Approval</h4>
            <ul>
              <li>Prepare ethics application using this design</li>
              <li>Address informed consent and confidentiality</li>
              <li>Plan data storage and protection</li>
            </ul>
          </div>
          <div className="next-step-card">
            <h4>3. Write Your Methodology</h4>
            <ul>
              <li>Use this report as your framework</li>
              <li>Paraphrase and adapt to your context</li>
              <li>Cite primary sources (Creswell, Saunders, etc.)</li>
            </ul>
          </div>
          <div className="next-step-card">
            <h4>4. Pre-Registration</h4>
            <ul>
              <li>Register your design and analysis plan on the Open Science Framework (OSF)</li>
              <li>Enhances transparency and credibility</li>
              <li>Protects against HARKing and p-hacking</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="resources-section">
        <h3>Recommended Resources</h3>
        <div className="resources-grid">
          <div className="resource-category">
            <h4>Books</h4>
            <ul>
              <li>Creswell & Creswell (2023) - Research Design (5th ed.)</li>
              <li>Saunders et al. (2019) - Research Methods for Business Students (8th ed.)</li>
              <li>Field (2024) - Discovering Statistics Using R/SPSS</li>
              <li>Tashakkori & Teddlie (2021) - SAGE Handbook of Mixed Methods</li>
            </ul>
          </div>
          <div className="resource-category">
            <h4>Online</h4>
            <ul>
              <li>SAGE Research Methods - Comprehensive methodology database</li>
              <li>Statistics Hell - Statistical consulting blog</li>
              <li>The Analysis Factor - Statistical resources</li>
              <li>ResearchGate - Connect with researchers</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalReport;
